import { AxiosResponse } from "axios";
import { EmployeePermissions } from "@snackpass/snackpass-types";

import { ErrorWithCause } from "src/utils/errors";
import {
    SnackpassPermissions,
    TeamMemberAccess,
} from "#settings/settings-permissions/types";

import { client } from "../client";

/**
 * `/stores/:id/staff` — the Team page's resource.
 *
 * One id (the staff row), one shape (`TeamMember`), and access as a field. Device-only
 * people are created here; dashboard access is granted by invite, see `api.admins.sendInvite`.
 * Responses are parsed with zod in `useStoreStaff`, so they are `unknown` here.
 */

export type StaffAccessTarget = Exclude<TeamMemberAccess, "none">;

export type CreateStaffBody = {
    access: "device";
    firstName: string;
    lastName: string;
    pin: string;
    role?: string;
    wage?: number;
    phone?: string;
    email?: string;
    identifier?: string;
    permissions?: Partial<EmployeePermissions>;
};

export type UpdateStaffBody = {
    /** Omit to leave the person's access as it is. */
    access?: StaffAccessTarget;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    role?: string;
    identifier?: string;
    wage?: number;
    pin?: string;
    /** Dashboard permissions, legacy flags as booleans. */
    permissions?: Partial<SnackpassPermissions>;
    /** Register permissions. */
    employeePermissions?: Partial<EmployeePermissions>;
};

export class Staff {
    static async list(storeId: string): Promise<
        AxiosResponse<{
            members: unknown[];
            invites: unknown[];
            source: string;
        }>
    > {
        return client.get(`/stores/${storeId}/staff`).catch((cause) => {
            throw new ErrorWithCause(
                `api.Staff.list: GET to /stores/${storeId}/staff failed`,
                cause,
            );
        });
    }

    static async create(
        storeId: string,
        body: CreateStaffBody,
    ): Promise<AxiosResponse<{ member: unknown }>> {
        return client.post(`/stores/${storeId}/staff`, body).catch((cause) => {
            throw new ErrorWithCause(
                `api.Staff.create: POST to /stores/${storeId}/staff failed`,
                cause,
            );
        });
    }

    static async update(
        storeId: string,
        staffId: string,
        body: UpdateStaffBody,
    ): Promise<AxiosResponse<{ member: unknown; message: string }>> {
        return client
            .patch(`/stores/${storeId}/staff/${staffId}`, body)
            .catch((cause) => {
                throw new ErrorWithCause(
                    `api.Staff.update: PATCH to /stores/${storeId}/staff/${staffId} failed`,
                    cause,
                );
            });
    }

    static async remove(
        storeId: string,
        staffId: string,
    ): Promise<AxiosResponse<unknown>> {
        return client
            .delete(`/stores/${storeId}/staff/${staffId}`)
            .catch((cause) => {
                throw new ErrorWithCause(
                    `api.Staff.remove: DELETE to /stores/${storeId}/staff/${staffId} failed`,
                    cause,
                );
            });
    }
}
